import React from "react";
import PropTypes from "prop-types";
import ImmutablePropTypes from 'react-immutable-proptypes';
import Slider from "react-slick";

import Component from "components/component";
import {
  isSelectable,
  shouldOptionRowUpdate,
  updateSliderOnOpenToggle
} from "lib/helpers";

import DefaultOption from "./default-option";
import DefaultOptionSelection from "./default-option-selection";
import SelectionPrompt from "./selection-prompt";
import { INPUT_HEIGHT } from "./com-details-form.jsx";
// import horizontalDimensionLineSrc from "./horizontal-dimension-line.svg"
import horizontalDimensionLineSrc from "./Horizontal_GREEN.svg"

const Orientation = {
  LAF: 'laf',
  RAF: 'raf',
};

const letters = ['A', 'B', 'C']
const nameRegex = /(.*)_(?:laf|raf)$/;
const cSideOptionValueNames = [
  'partial_back_u_sectional',
  'standard_back_u_sectional',
  'chaise_end_u_sectional',
  'chaise_end_and_standard_back_u_sectional_raf',
  'chaise_end_and_standard_back_u_sectional_laf',
  'partial_back_and_chaise_end_u_sectional_raf',
  'partial_back_and_chaise_end_u_sectional_laf',
  'standard_and_partial_back_u_sectional_raf',
  'standard_and_partial_back_u_sectional_laf',
];

const getPairId = (optionValueId, optionValues) => {
  const optionValue = optionValues.find(ov => ov.get('id') === optionValueId);

  if (!optionValue) {
    return;
  }

  const optionValueName = optionValue.get('name');
  if (optionValueName.endsWith(Orientation.RAF)) {
    const pairName = optionValueName.replace(Orientation.RAF, Orientation.LAF);
    const pair = optionValues.find(ov => ov.get('name') === pairName);

    if (pair) {
      return pair.get('id');
    }
  } else if (optionValueName.endsWith(Orientation.LAF)) {
    const pairName = optionValueName.replace(Orientation.LAF, Orientation.RAF);
    const pair = optionValues.find(ov => ov.get('name') === pairName);

    if (pair) {
      return pair.get('id');
    }
  }
}

const settings = {
  speed: 500,
  dots: false,
  arrows: false,
  infinite: false,
  draggable: true,
  focusOnSelect: false,
  slidesPerRow: 3,
  rows: 10,
  swipeToSlide: true,
  centerMode: false
};

export default class SectionalOptionRow extends Component {
  static propTypes = {
    optionType: ImmutablePropTypes.map,
    optionValues: ImmutablePropTypes.map.isRequired,
    selection: ImmutablePropTypes.map,
    handleClick: PropTypes.func.isRequired,
    selections: ImmutablePropTypes.map,
    optionValueRestrictions: ImmutablePropTypes.map,
    quantityCount: PropTypes.number.isRequired
  };

  constructor(props) {
    super(props);

    const name = this.props.selection.get('name');

    this.state = {
      isOpen: false,
      orientation: name.endsWith(Orientation.RAF) ? Orientation.RAF : Orientation.LAF,
      hasFiredInitialHandleClick: false,
    };
  }

  // TODO: this is really dumb; we should change it
  shouldComponentUpdate(newProps, newState) {
    if (this.state.orientation !== newState.orientation) return true;
    if (this.props.sideADimension !== newProps.sideADimension) return true;
    if (this.props.sideBDimension !== newProps.sideBDimension) return true;
    if (this.props.sideCDimension !== newProps.sideCDimension) return true;

    return shouldOptionRowUpdate(newProps, newState, this);
  }

  toggleOpen = e => {
    if (e) e.preventDefault();
    this.setState({ isOpen: !this.state.isOpen });
  };

  handleRightClick = async (e) => {
    if (e) e.preventDefault();

    await this.setState({ orientation: Orientation.RAF })

    this.togglePairedOptionValue(Orientation.RAF, e);
  };

  handleLeftClick = async (e) => {
    if (e) e.preventDefault();

    await this.setState({ orientation: Orientation.LAF })

    // If current orientation is LAF, ignore
    this.togglePairedOptionValue(Orientation.LAF, e);
  };

  togglePairedOptionValue = (desiredOrientation, e) => {
    const currentSelectionName = this.props.selection.get('name');

    if (currentSelectionName.endsWith(Orientation.RAF)) {
      if (desiredOrientation === Orientation.RAF) {
        return;
      }

      const newSelectionName = currentSelectionName.replace(Orientation.RAF, Orientation.LAF);
      const newSelection = this.props.optionValues.find(ov => ov.get('name') === newSelectionName);
      if (newSelection) {
        this.handleClick(newSelection.get('id'), e);
      }
    } else if (currentSelectionName.endsWith(Orientation.LAF)) {
      if (desiredOrientation === Orientation.LAF) {
        return;
      }

      const newSelectionName = currentSelectionName.replace(Orientation.LAF, Orientation.RAF);
      const newSelection = this.props.optionValues.find(ov => ov.get('name') === newSelectionName);
      if (newSelection) {
        this.handleClick(newSelection.get('id'), e);
      }
    }
  }

  handleClick = (id, e) => {
    const optionTypeId = this.props.selection.get('optionTypeId');
    let asShown = false;

    const defaultConfiguration = this.props.product.get('defaultConfiguration');
    const defaultConfigurationOptionValueId = defaultConfiguration.toObject()?.[optionTypeId];
    const pairId = getPairId(defaultConfigurationOptionValueId, this.props.optionValues);

    if ([defaultConfigurationOptionValueId, pairId].includes(id)) {
      asShown = true;
    }

    if (asShown) {
      this.props.sectionalLayoutIsCustomChanged(false);
    } else {
      this.props.sectionalLayoutIsCustomChanged(true);
    }

    this.props.handleClick(id, e);
  }

  getLeftSelection = () => {
    let salientOptionValue = this.props.selection;

    if (salientOptionValue.get('name').endsWith(Orientation.RAF)) {
      const selectionName = this.props.selection.get('name');
      const leftSelectionName = selectionName.replace(Orientation.RAF, Orientation.LAF);

      const leftSelection = this.props.optionValues.find(ov => ov.get('name') === leftSelectionName);

      salientOptionValue = leftSelection;
    }

    return salientOptionValue;
  }

  getRightSelection = () => {
    let salientOptionValue = this.props.selection;

    if (salientOptionValue.get('name').endsWith(Orientation.LAF)) {
      const selectionName = this.props.selection.get('name');
      const leftSelectionName = selectionName.replace(Orientation.LAF, Orientation.RAF);

      const leftSelection = this.props.optionValues.find(ov => ov.get('name') === leftSelectionName);

      salientOptionValue = leftSelection;
    }

    return salientOptionValue;
  }

  getSymetricalImageSrc = () => {
    let salientOptionValue = this.props.selection;

    return getVariantUrl(salientOptionValue);
  }

  UNSAFE_componentDidUpdate(oldProps, oldState) {
    updateSliderOnOpenToggle(oldState, this);
  }

  render() {
    if (this.props.optionValues.isEmpty()) return null;

    const { isOpen, orientation } = this.state;
    const {
      product,
      optionType,
      optionValues,
      selection,
      handleClick,
      selections,
      optionValueRestrictions,
      quantityCount,
      sideADimension,
      sideBDimension,
      sideCDimension,
      sectionalLayoutIsCustom,
      sideADimensionChanged,
      sideBDimensionChanged,
      sideCDimensionChanged,
      sectionalLayoutIsCustomChanged,
      userRole,
      openModal,
      isEditing,
      mobile,
    } = this.props;

    // Uncomment to disable for mobile
    // if (mobile) {
    //   return null;
    // }

    const optionName = optionType.get("displayName");
    const toggleClass = isOpen ? "toggle-row open" : "toggle-row";
    const selectionIsPresent = !!selection;
    let selectionId,
      selectionName,
      selectionUpchargeAmount,
      selectionImage,
      selectionVariantImage,
      selectionDisplayUpchargeAmount;
    let selectionIndex = 0;
    let uShaped = false;

    // const isTradeUser = userRole === 'trade_user';
    const defaultConfiguration = product.get('defaultConfiguration');

    if (selectionIsPresent) {
      selectionId = selection.get("id");
      selectionName = selection.get("displayName");
      selectionUpchargeAmount = selection.get("upchargeAmount");
      selectionImage = selection.get("thumbnailImage");
      selectionVariantImage = getVariantUrl(selection);
      selectionIndex = optionValues
        .valueSeq()
        .findIndex(ov => ov.get("id") === selectionId);
      selectionDisplayUpchargeAmount = selection.get("displayUpchargeAmount");
      uShaped = cSideOptionValueNames.includes(selection.get('name'))

      if (selectionId && !this.state.hasFiredInitialHandleClick) {
        this.setState({
          hasFiredInitialHandleClick: true,
        },() => {
          this.handleClick(selectionId);
        });
      }
    }

    const handleClickPaired = (optionValueId, e) => {
      const optionValue = optionValues.find(v => v.get('id') === optionValueId);
      this.handleClick(optionValueId, e);
    }

    const selectedOptionValues = optionValues.filter(ov => {
      const name = ov.get('name');

      return name.endsWith(orientation) || (!name.endsWith(Orientation.LAF) && !name.endsWith(Orientation.RAF));
    })

    const pairedOptionValues = optionValues.filter(ov => {
      const name = ov.get('name');

      return name.endsWith(orientation);
    });

    const nonPairedOptionValues = optionValues.filter(ov => {
      const name = ov.get('name');

      return !name.endsWith(Orientation.LAF) && !name.endsWith(Orientation.RAF);
    });

    const nonPairedIsSelected = nonPairedOptionValues.some(ov => {
      return selection.get('id') === ov.get('id');
    });

    const commonStyles = {
      border: '2px solid transparent',
      padding: 10,
      position: 'relative',
      marginTop: '5px',
      paddingTop: 20,
    };

    const lafStyles = { ...commonStyles };
    const lafImageStyles = {};
    const rafStyles = { ...commonStyles };
    const rafImageStyles = {};

    if (nonPairedIsSelected) {
      lafImageStyles.opacity = '50%';
      rafImageStyles.opacity = '50%';
    } else {
      lafStyles.cursor = 'pointer';
      rafStyles.cursor = 'pointer';

      if (orientation === Orientation.LAF) {
        lafStyles.border = '2px solid #BD965F';
        lafStyles.backgroundColor = '#F4F4F4';
      } else if (orientation === Orientation.RAF) {
        rafStyles.border = '2px solid #BD965F';
        rafStyles.backgroundColor = '#F4F4F4';
      }
    }

    const optionTypeId = selection.get('optionTypeId');
    const defaultConfigurationOptionValueId = defaultConfiguration.toObject()?.[optionTypeId];
    const pairId = getPairId(defaultConfigurationOptionValueId, optionValues);

    const isCustom = sectionalLayoutIsCustom || sideADimension || sideBDimension || sideCDimension;
    const asShown = !isCustom;

    let asShownClasses = 'large';
    let customClasses = 'large';

    if (asShown) {
      customClasses = `${customClasses} secondary`;
    } else {
      asShownClasses = `${asShownClasses} secondary`;
    }

    const handleAsShownClick = (e) => {
      e.preventDefault();

      if (defaultConfigurationOptionValueId) {
        this.handleClick(defaultConfigurationOptionValueId, e);

        const defaultConfigurationOptionValue = optionValues.find(ov => ov.get('id') === defaultConfigurationOptionValueId);

        if (!defaultConfigurationOptionValue) {
          return;
        }

        const defaultName = defaultConfigurationOptionValue.get('name');

        if (!defaultName) {
          return;
        }

        if (defaultName.endsWith(Orientation.LAF)) {
          this.handleLeftClick(e)
        } else if (defaultName.endsWith(Orientation.RAF)) {
          this.handleRightClick(e)
        }
      }
    }

    const handleCustomClick = (e) => {
      e.preventDefault();

      const optionValue = optionValues.filter(ov => {
        return (
          ov.get('id') !== defaultConfigurationOptionValueId && ov.get('id') !== pairId
        );
      }).first();

      if (!optionValue) {
        return;
      }

      const optionValueName = optionValue.get('name');
      if (!optionValueName) {
        return;
      }

      if (optionValueName.endsWith(Orientation.LAF)) {
        this.handleLeftClick(e)
      } else if (optionValueName.endsWith(Orientation.RAF)) {
        this.handleRightClick(e)
      }

      this.handleClick(optionValue.get('id'), e);
    }

    const handleViewDrawingClick = (e) => {
      e.preventDefault();

      const lafThumbnail = product.get('handedDrawingPaths').find(path => {
        return path.includes(`${product.get('slug')}-laf`);
      });
      const rafThumbnail = product.get('handedDrawingPaths').find(path => {
        return path.includes(`${product.get('slug')}-raf`);
      });

      if (openModal) {
        openModal("sectional-drawings", {
          thumbnail: orientation === Orientation.LAF ? lafThumbnail : rafThumbnail,
        });
      }
    }

    const leftSelection = this.getLeftSelection();
    const rightSelection = this.getRightSelection();

    const leftImageSrc = getVariantUrl(leftSelection);
    const rightImageSrc = getVariantUrl(rightSelection);
    const symetricalImageSrc = this.getSymetricalImageSrc();

    return (
      <div>
        <div className="product-options-title-row desktop-only">
          <p className="lined-title">
            <span>{optionName}</span>
          </p>
        </div>

        <div className="product-options-row option-columns three-columns">
          <div className="product-options-wrapper-mobile mobile-only">
            <a href="#" className={toggleClass} onClick={this.toggleOpen} />

            {!isOpen && !selectionIsPresent && (
              <div>
                <p className="option-name">{optionName}</p>
                <SelectionPrompt name={selectionName} />
              </div>
            )}

            {!isOpen && selectionIsPresent && (
              <DefaultOptionSelection
                optionName={optionName}
                selectionName={selectionName}
                upchargeAmount={selectionUpchargeAmount}
                thumbnailImage={selectionVariantImage}
                displayUpchargeAmountOverride={selectionDisplayUpchargeAmount}
                quantityCount={quantityCount}
                showQuantity={
                  selectionUpchargeAmount > 0 && quantityCount > 1
                }
              />
            )}

            <div style={{ display: isOpen ? "block" : "none" }}>
              <p className="option-name">{optionName}</p>

              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 50,
                marginBottom: 30,
                marginTop: 40,
              }}>
                <button onClick={handleAsShownClick} className={asShownClasses}>As Shown</button>
                <button onClick={handleCustomClick} className={customClasses}>Custom</button>
              </div>

              <div className="product-options-slider-wrapper">
                <Slider {...settings} slickGoTo={selectionIndex} ref="slider">
                  {selectedOptionValues.valueSeq().map((optionValue, key) => {
                    const id = optionValue.get("id");
                    const selectable = isSelectable(
                      selections,
                      optionValueRestrictions,
                      id
                    );

                    const variantUrl = getVariantUrl(optionValue);

                    return (
                      <div key={id}>
                        <DefaultOption
                          optionValue={optionValue}
                          thumbnailImageOverride={variantUrl}
                          isSelected={selectionId === id}
                          handleClick={this.handleClick}
                          selectable={selectable}
                          titleStyles={{ height: '70px' }}
                          inSlider
                        />
                      </div>
                    );
                  })}
                </Slider>

                <div style={{
                  overflow: 'hidden',
                  textAlign: 'center',
                  marginBottom: 20,
                  marginTop: 40,
                }}>
                  <p className="lined-title">
                    <span>Orientation</span>
                  </p>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 50,
                  marginBottom: 40,
                }}>
                  {nonPairedIsSelected ? (
                    <div className="orientation-drawing" style={commonStyles}>
                      <img src={symetricalImageSrc} />
                      <>
                        <div className='hr-dimension-container-left'>
                          <span className='p-style'>A</span>
                        </div>
                        <div className='hr-dimension-container-top'>
                          <span className='p-style'>B</span>
                          <img src={horizontalDimensionLineSrc} />
                        </div>
                        <div className='hr-dimension-container-right'>
                          <span className='p-style'>C</span>
                        </div>
                      </>
                    </div>
                  ) : (
                    <>
                      {!!leftSelection && (
                        <div className="orientation-drawing" style={lafStyles} onClick={this.handleLeftClick}>
                          <img style={lafImageStyles} src={leftImageSrc} />
                      <p style={{ textAlign: 'center', marginTop: 15 }}>{uShaped ? 'U' : 'L'}-Shaped (LAF)</p>
                          {cSideOptionValueNames.includes(leftSelection.get('name')) ? (
                            <>
                              <div className='hr-dimension-container-left'>
                                <span className='p-style'>A</span>
                              </div>
                              <div className='hr-dimension-container-top'>
                                <span className='p-style'>B</span>
                                <img src={horizontalDimensionLineSrc} />
                              </div>
                              <div className='hr-dimension-container-right'>
                                <span className='p-style'>C</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className='hr-dimension-container-top'>
                                <span className='p-style'>A</span>
                                <img src={horizontalDimensionLineSrc} />
                              </div>

                              <div className='hr-dimension-container-left'>
                                <span className='p-style'>B</span>
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {!!rightSelection && (
                        <div className="orientation-drawing" style={rafStyles} onClick={this.handleRightClick}>
                          <img style={rafImageStyles} src={rightImageSrc} />
                          <p style={{ textAlign: 'center', marginTop: 15 }}>{uShaped ? 'U' : 'L'}-Shaped (RAF)</p>
                          {cSideOptionValueNames.includes(rightSelection.get('name')) ? (
                            <>
                              <div className='hr-dimension-container-left'>
                                <span className='p-style'>A</span>
                              </div>
                              <div className='hr-dimension-container-top'>
                                <span className='p-style'>B</span>
                                <img src={horizontalDimensionLineSrc} />
                              </div>
                              <div className='hr-dimension-container-right'>
                                <span className='p-style'>C</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className='hr-dimension-container-top'>
                                <span className='p-style'>A</span>
                                <img src={horizontalDimensionLineSrc} />
                              </div>
                              <div className='hr-dimension-container-right'>
                                <span className='p-style'>B</span>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {asShown && (
                  <button
                    onClick={handleViewDrawingClick}
                    className="large"
                    style={{
                      margin: 'auto',
                      display: 'block',
                      marginBottom: 50,
                    }}
                  >View Drawing</button>
                )}

                {(!isEditing) && (
                  <>
                    <div style={{
                      overflow: 'hidden',
                      textAlign: 'center',
                      marginBottom: 50,
                    }}>
                      <p className="lined-title">
                        <span>Dimensions</span>
                      </p>
                    </div>

                    <form>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          flexDirection: 'column',
                        }}
                        className="input-wrap"
                      >
                        <div style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 20,
                          marginBottom: 20,
                        }}>
                          <p>Side A Dimensions</p>

                          <div style={{ width: 200, position: 'relative' }}>
                            <input
                              style={{
                                fontSize: '0.8125em',
                                paddingRight: 65,
                                textAlign: 'right',
                                height: INPUT_HEIGHT,
                              }}
                              className="text-input"
                              type="number"
                              value={sideADimension || ""}
                              onKeyPress={(event) => {
                                if (!/[0-9]/.test(event.key)) {
                                  event.preventDefault();
                                }
                              }}
                              onChange={(e) => {
                                sideADimensionChanged(e.currentTarget.value);
                              }}
                            />
                            <span style={{
                              fontSize: '0.8125em',
                              color: 'grey',
                              position: 'absolute',
                              right: 10,
                              top: '29%',
                            }}>inches</span>
                          </div>
                        </div>

                        <div style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 20,
                          marginBottom: 20,
                        }}>
                          <p>Side B Dimensions</p>

                          <div style={{ width: 200, position: 'relative' }}>
                            <input
                              style={{
                                fontSize: '0.8125em',
                                paddingRight: 65,
                                textAlign: 'right',
                                height: INPUT_HEIGHT,
                              }}
                              className="text-input"
                              type="number"
                              value={sideBDimension || ""}
                              onKeyPress={(event) => {
                                if (!/[0-9]/.test(event.key)) {
                                  event.preventDefault();
                                }
                              }}
                              onChange={(e) => {
                                sideBDimensionChanged(e.currentTarget.value);
                              }}
                            />
                            <span style={{
                              fontSize: '0.8125em',
                              color: 'grey',
                              position: 'absolute',
                              right: 10,
                              top: '29%',
                            }}>inches</span>
                          </div>
                        </div>

                        <div style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 20,
                          marginBottom: 20,
                          color: cSideOptionValueNames.includes(selection.get('name')) ? 'inherit' : '#aaa',
                        }}>
                          <p>Side C Dimensions</p>
                          <div style={{ width: 200, position: 'relative' }}>
                            <input
                              style={{
                                fontSize: '0.8125em',
                                paddingRight: 65,
                                textAlign: 'right',
                                height: INPUT_HEIGHT,
                              }}
                              className="text-input"
                              type="number"
                              disabled={!cSideOptionValueNames.includes(selection.get('name'))}
                              value={sideCDimension || ""}
                              onKeyPress={(event) => {
                                if (!/[0-9]/.test(event.key)) {
                                  event.preventDefault();
                                }
                              }}
                              onChange={(e) => {
                                sideCDimensionChanged(e.currentTarget.value);
                              }}
                            />
                            <span style={{
                              fontSize: '0.8125em',
                              color: 'grey',
                              position: 'absolute',
                              right: 10,
                              top: '29%',
                            }}>inches</span>
                          </div>
                        </div>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="desktop-only" style={{ width: '100%' }}>
            <>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 50,
                marginBottom: 20,
              }}>
                <button onClick={handleAsShownClick} className={asShownClasses}>As Shown</button>
                <button onClick={handleCustomClick} className={customClasses}>Custom</button>
              </div>
              <div className="options-container" style={{
                display: 'flex',
                justifyContent: 'center',
              }}>
                {nonPairedOptionValues.valueSeq().map((optionValue, key) => {
                  const id = optionValue.get("id");
                  const selectable = isSelectable(
                    selections,
                    optionValueRestrictions,
                    id
                  );
                  const noMargin = (key + 1) % 3 === 0;
                  const variantUrl = getVariantUrl(optionValue);

                  return (
                    <DefaultOption
                      key={id}
                      optionValue={optionValue}
                      thumbnailImageOverride={variantUrl}
                      isSelected={selectionId === id}
                      handleClick={this.handleClick}
                      selectable={selectable}
                      noMargin={noMargin}
                      containerStyles={{ flex: '0 0 30%' }}
                    />
                  );
                })}
                {pairedOptionValues.valueSeq().map((optionValue, key) => {
                  const id = optionValue.get("id");
                  const selectable = isSelectable(
                    selections,
                    optionValueRestrictions,
                    id
                  );
                  const noMargin = (key + 1) % 3 === 0;
                  const variantUrl = getVariantUrl(optionValue);

                  return (
                    <DefaultOption
                      key={id}
                      displayNameOverride={optionValue.get('displayName')?.split(' -')?.[0]}
                      thumbnailImageOverride={variantUrl}
                      optionValue={optionValue}
                      isSelected={selectionId === id}
                      handleClick={handleClickPaired}
                      selectable={selectable}
                      noMargin={noMargin}
                      containerStyles={{ flex: '0 0 30%' }}
                    />
                  );
                })}
              </div>
            </>


            <div style={{
              overflow: 'hidden',
              textAlign: 'center',
              marginBottom: 20,
            }}>
              <p className="lined-title">
                <span>Orientation</span>
              </p>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 50,
              marginBottom: 40,
            }}>
              {nonPairedIsSelected ? (
                <div className="orientation-drawing" style={commonStyles}>
                  <img src={symetricalImageSrc} />
                  <>
                    <div className='hr-dimension-container-left'>
                      <span className='p-style'>A</span>
                    </div>
                    <div className='hr-dimension-container-top'>
                      <span className='p-style'>B</span>
                      <img src={horizontalDimensionLineSrc} />
                    </div>
                    <div className='hr-dimension-container-right'>
                      <span className='p-style'>C</span>
                    </div>
                  </>
                </div>
              ) : (
                <>
                  {!!leftSelection && (
                    <div className="orientation-drawing" style={lafStyles} onClick={this.handleLeftClick}>
                      <img style={lafImageStyles} src={leftImageSrc} />
                  <p style={{ textAlign: 'center', marginTop: 15 }}>{uShaped ? 'U' : 'L'}-Shaped (LAF)</p>
                      {cSideOptionValueNames.includes(leftSelection.get('name')) ? (
                        <>
                          <div className='hr-dimension-container-left'>
                            <span className='p-style'>A</span>
                          </div>
                          <div className='hr-dimension-container-top'>
                            <span className='p-style'>B</span>
                            <img src={horizontalDimensionLineSrc} />
                          </div>
                          <div className='hr-dimension-container-right'>
                            <span className='p-style'>C</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className='hr-dimension-container-top'>
                            <span className='p-style'>A</span>
                            <img src={horizontalDimensionLineSrc} />
                          </div>

                          <div className='hr-dimension-container-left'>
                            <span className='p-style'>B</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {!!rightSelection && (
                    <div className="orientation-drawing" style={rafStyles} onClick={this.handleRightClick}>
                      <img style={rafImageStyles} src={rightImageSrc} />
                      <p style={{ textAlign: 'center', marginTop: 15 }}>{uShaped ? 'U' : 'L'}-Shaped (RAF)</p>
                      {cSideOptionValueNames.includes(rightSelection.get('name')) ? (
                        <>
                          <div className='hr-dimension-container-left'>
                            <span className='p-style'>A</span>
                          </div>
                          <div className='hr-dimension-container-top'>
                            <span className='p-style'>B</span>
                            <img src={horizontalDimensionLineSrc} />
                          </div>
                          <div className='hr-dimension-container-right'>
                            <span className='p-style'>C</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className='hr-dimension-container-top'>
                            <span className='p-style'>A</span>
                            <img src={horizontalDimensionLineSrc} />
                          </div>
                          <div className='hr-dimension-container-right'>
                            <span className='p-style'>B</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {asShown && (
              <button
                onClick={handleViewDrawingClick}
                className="large"
                style={{
                  margin: 'auto',
                  display: 'block',
                  marginBottom: 50,
                }}
              >View Drawing</button>
            )}

            {(!isEditing) && (
              <>
                <div style={{
                  overflow: 'hidden',
                  textAlign: 'center',
                  marginBottom: 50,
                }}>
                  <p className="lined-title">
                    <span>Dimensions</span>
                  </p>
                </div>

                <form>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      flexDirection: 'column',
                    }}
                    className="input-wrap"
                  >
                    <div style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 20,
                      marginBottom: 20,
                    }}>
                      <p>Side A Dimensions</p>

                      <div style={{ width: 200, position: 'relative' }}>
                        <input
                          style={{
                            fontSize: '0.8125em',
                            paddingRight: 65,
                            textAlign: 'right',
                            height: INPUT_HEIGHT,
                          }}
                          className="text-input"
                          type="number"
                          value={sideADimension || ""}
                          onKeyPress={(event) => {
                            if (!/[0-9]/.test(event.key)) {
                              event.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            sideADimensionChanged(e.currentTarget.value);
                          }}
                        />
                        <span style={{
                          fontSize: '0.8125em',
                          color: 'grey',
                          position: 'absolute',
                          right: 10,
                          top: '29%',
                        }}>inches</span>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 20,
                      marginBottom: 20,
                    }}>
                      <p>Side B Dimensions</p>

                      <div style={{ width: 200, position: 'relative' }}>
                        <input
                          style={{
                            fontSize: '0.8125em',
                            paddingRight: 65,
                            textAlign: 'right',
                            height: INPUT_HEIGHT,
                          }}
                          className="text-input"
                          type="number"
                          value={sideBDimension || ""}
                          onKeyPress={(event) => {
                            if (!/[0-9]/.test(event.key)) {
                              event.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            sideBDimensionChanged(e.currentTarget.value);
                          }}
                        />
                        <span style={{
                          fontSize: '0.8125em',
                          color: 'grey',
                          position: 'absolute',
                          right: 10,
                          top: '29%',
                        }}>inches</span>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 20,
                      marginBottom: 20,
                      color: cSideOptionValueNames.includes(selection.get('name')) ? 'inherit' : '#aaa',
                    }}>
                      <p>Side C Dimensions</p>
                      <div style={{ width: 200, position: 'relative' }}>
                        <input
                          style={{
                            fontSize: '0.8125em',
                            paddingRight: 65,
                            textAlign: 'right',
                            height: INPUT_HEIGHT,
                          }}
                          className="text-input"
                          type="number"
                          disabled={!cSideOptionValueNames.includes(selection.get('name'))}
                          value={sideCDimension || ""}
                          onKeyPress={(event) => {
                            if (!/[0-9]/.test(event.key)) {
                              event.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            sideCDimensionChanged(e.currentTarget.value);
                          }}
                        />
                        <span style={{
                          fontSize: '0.8125em',
                          color: 'grey',
                          position: 'absolute',
                          right: 10,
                          top: '29%',
                        }}>inches</span>
                      </div>
                    </div>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}

function getVariantUrl(optionValue) {
  if (!optionValue) {
    return;
  }

  const variant = optionValue.get("variant");

  if (!variant) {
    return;
  }

  const lineDrawings = variant.get("lineDrawings");

  if (lineDrawings.size === 0) {
    return;
  }

  return lineDrawings.first().get("url");
}
